import { useTranslation } from 'react-i18next'

import { Badge, Card, Overline, Section, SectionTitle } from '@/design-system/primitives'
import { Button } from '@/design-system/Button'
import { useTilt } from '@/design-system/effects'
import { cn } from '@/lib/cn'

import { EDUCATION_SUGGESTIONS } from './profile'
import { formatScore, formatShare } from './score'
import type {
  PublicAward,
  PublicCandidate,
  PublicCategory,
  PublicContestStandings,
  PublicRoundVotes,
} from './types'

/**
 * Galerie Miss & Master.
 *
 * Ce qui s'affiche ici vient d'un contrat serveur qui ne contient ni e-mail,
 * ni téléphone, ni nom civil. La confidentialité ne repose donc pas sur la
 * discipline de ce composant.
 */
export function CompetitionSection({
  categories,
  candidates,
  onApply,
  title,
}: {
  categories: PublicCategory[]
  candidates: PublicCandidate[]
  onApply: (categoryId: string) => void
  /**
   * Le titre, réécrivable (bloc `site.competition` dans Contenus).
   * `undefined` : texte de secours ; vide : pas de titre.
   */
  title?: string | undefined
}) {
  const { t } = useTranslation()

  if (categories.length === 0) return null
  const heading = title ?? t('competition.title')

  return (
    <Section id="competition">
      <Overline>{t('competition.overline')}</Overline>
      {heading ? <SectionTitle>{heading}</SectionTitle> : null}

      <div className="mt-6 space-y-8">
        {categories.map((category) => {
          const entrants = candidates.filter(
            (candidate) => candidate.competition_category_id === category.id,
          )

          return (
            <section key={category.id}>
              <div className="flex flex-wrap items-end justify-between gap-6">
                <div className="max-w-2xl">
                  <h3 className="font-display text-heading-md text-content-primary">
                    {category.name}
                  </h3>
                  {category.description ? (
                    <p className="mt-3 text-body-lg text-content-secondary">
                      {category.description}
                    </p>
                  ) : null}
                  <Eligibility category={category} />
                </div>

                {category.applications_open ? (
                  <Button onClick={() => onApply(category.id)}>{t('competition.apply')}</Button>
                ) : (
                  <Badge>{t('competition.closed')}</Badge>
                )}
              </div>

              {entrants.length > 0 ? (
                <ul className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {entrants.map((candidate) => (
                    <CandidateCard key={candidate.id} candidate={candidate} />
                  ))}
                </ul>
              ) : (
                <p className="mt-6 text-content-muted">{t('competition.noCandidates')}</p>
              )}
            </section>
          )
        })}
      </div>
    </Section>
  )
}

function Eligibility({ category }: { category: PublicCategory }) {
  const { t } = useTranslation()
  const { min_age: min, max_age: max } = category.eligibility

  const parts: string[] = []
  if (min && max) parts.push(t('competition.ageRange', { min, max }))
  else if (min) parts.push(t('competition.ageFrom', { min }))
  else if (max) parts.push(t('competition.ageUntil', { max }))

  if (category.seats_remaining !== null) {
    parts.push(t('competition.seatsLeft', { count: category.seats_remaining }))
  }

  if (parts.length === 0) return null
  return <p className="mt-3 text-caption text-content-muted">{parts.join(' · ')}</p>
}

function CandidateCard({ candidate }: { candidate: PublicCandidate }) {
  const { t } = useTranslation()
  // Le dossard fait une adresse lisible : `#candidat-07`. L'identifiant
  // technique marcherait aussi, mais un lien qu'on lit à voix haute au micro
  // vaut mieux qu'une suite de trente-six caractères.
  const anchor = `candidat-${
    candidate.candidate_number !== null
      ? String(candidate.candidate_number).padStart(2, '0')
      : candidate.id
  }`

  // La carte s'incline sous la souris : on a envie de la prendre en main.
  const tiltRef = useTilt<HTMLLIElement>(6)

  return (
    // `min-w-0` : sans lui, un mot très long dans l'adage élargirait la
    // colonne de la grille au-delà de l'écran d'un téléphone.
    <li ref={tiltRef} id={anchor} className="min-w-0 scroll-mt-24">
      <Card
        interactive
        className={cn(
          'h-full overflow-hidden p-0',
          candidate.status === 'winner' && 'hairline-champagne',
          candidate.status === 'eliminated' && 'opacity-60',
        )}
      >
        <div className="relative aspect-[3/4] bg-bg-overlay">
          {candidate.photo_url ? (
            <img
              src={candidate.photo_url}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full items-center justify-center font-display text-display-lg text-content-muted">
              {candidate.candidate_number ?? '?'}
            </div>
          )}

          {candidate.candidate_number !== null ? (
            <span className="absolute left-3 top-3 rounded-full bg-bg-base/85 px-3 py-1 font-mono text-caption tabular-nums text-champagne-300">
              {String(candidate.candidate_number).padStart(2, '0')}
            </span>
          ) : null}
        </div>

        <div className="p-5">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-display text-heading-sm text-content-primary">
              {candidate.name}
            </h4>
            {candidate.status === 'winner' ? (
              <Badge tone="champagne">{t('competition.winner')}</Badge>
            ) : candidate.status === 'finalist' ? (
              <Badge tone="ember">{t('competition.finalist')}</Badge>
            ) : null}
          </div>

          <CandidateProfile candidate={candidate} />

          {candidate.bio ? (
            <p className="mt-3 line-clamp-4 text-sm text-content-secondary">{candidate.bio}</p>
          ) : null}

          {Object.entries(candidate.socials).length > 0 ? (
            <p className="mt-3 text-caption text-champagne-300">
              {Object.values(candidate.socials).join(' · ')}
            </p>
          ) : null}

        </div>
      </Card>
    </li>
  )
}

/**
 * Le profil public : âge et activité, études, trois mots, adage.
 *
 * Chaque ligne n'apparaît que si elle est remplie : une candidature déposée
 * avant que le formulaire ne les demande n'affiche pas de « — » orphelins.
 * Les textes longs se coupent (`break-words`) : un adage sans espace ne doit
 * pas élargir la carte au-delà d'un écran de téléphone.
 */
function CandidateProfile({ candidate }: { candidate: PublicCandidate }) {
  const { t } = useTranslation()
  const headline = [
    candidate.age !== null ? t('competition.years', { count: candidate.age }) : null,
    candidate.activity,
  ].filter(Boolean)

  return (
    <>
      {headline.length > 0 ? (
        <p className="mt-1 break-words text-caption text-content-muted">
          {headline.join(' · ')}
        </p>
      ) : null}

      {candidate.education_level ? (
        <p className="mt-1 break-words text-caption text-content-muted">
          {t('candidateProfile.educationShown', { level: candidate.education_level })}
        </p>
      ) : null}

      {candidate.three_words.length > 0 ? (
        <ul
          className="mt-3 flex flex-wrap gap-1.5"
          aria-label={t('candidateProfile.threeWordsShown')}
        >
          {candidate.three_words.map((word) => (
            <li
              key={word}
              className="max-w-full break-words rounded-full border border-champagne-400/40 px-2.5 py-0.5 text-caption text-champagne-300"
            >
              {word}
            </li>
          ))}
        </ul>
      ) : null}

      {candidate.motto ? (
        <blockquote className="mt-3 break-words border-l-2 border-champagne-400/50 pl-3 text-sm italic text-content-secondary">
          {t('candidateProfile.quoted', { text: candidate.motto })}
        </blockquote>
      ) : null}
    </>
  )
}

/** Les suggestions de niveau d'études, dans la langue de la page. Le champ reste libre. */
export function EducationSuggestions({ id }: { id: string }) {
  const { t } = useTranslation()
  return (
    <datalist id={id}>
      {EDUCATION_SUGGESTIONS.map((key) => (
        <option key={key} value={t(`candidateProfile.education.${key}`)} />
      ))}
    </datalist>
  )
}

/** Podium — n'apparaît que si le serveur a envoyé des résultats révélés. */
export function AwardsSection({ awards }: { awards: PublicAward[] }) {
  const { t } = useTranslation()

  const announced = awards.filter((award) => award.results.length > 0)
  if (announced.length === 0) return null

  return (
    <Section id="results">
      <Overline>{t('awards.overline')}</Overline>
      <SectionTitle>{t('awards.title')}</SectionTitle>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {announced.map((award) => (
          <Card key={award.id} className="hairline-champagne bg-transparent">
            <h3 className="font-display text-heading-md text-champagne-300">{award.name}</h3>
            {award.prize ? (
              <p className="mt-2 text-sm text-content-muted">{award.prize}</p>
            ) : null}

            <ol className="mt-6 space-y-4">
              {award.results.map((result) => (
                <li key={result.rank} className="flex items-center gap-4">
                  <span className="w-8 shrink-0 font-display text-heading-md tabular-nums text-champagne-400">
                    {result.rank}
                  </span>
                  {result.photo_url ? (
                    <img
                      src={result.photo_url}
                      alt=""
                      className="h-12 w-12 rounded-full object-cover"
                      loading="lazy"
                    />
                  ) : null}
                  <span className="font-display text-heading-sm text-content-primary">
                    {result.name}
                  </span>
                  {result.candidate_number !== null ? (
                    <span className="font-mono text-caption text-content-muted">
                      #{result.candidate_number}
                    </span>
                  ) : null}
                </li>
              ))}
            </ol>
          </Card>
        ))}
      </div>
    </Section>
  )
}

/**
 * Le classement général de chaque titre — ce qui fait Miss et Master.
 *
 * Chaque tour est noté sur 100 : 70 viennent des voix du public, 30 des notes
 * du jury — poids lus dans la réponse (`public_weight` / `jury_weight`), pas
 * recopiés ici. Le titre se joue sur le cumul des trois tours (sur 300).
 *
 * Trois temps, et le serveur n'envoie que ce qui est public à chacun :
 *
 * 1. avant la proclamation — le titre existe, sans rien d'autre ;
 * 2. proclamé — le NOM du vainqueur, aucun chiffre ;
 * 3. scores publiés — le cumul, puis chaque tour, public et jury séparés.
 */
export function StandingsSection({ contests }: { contests: PublicContestStandings[] }) {
  const { t, i18n } = useTranslation()
  const score = (value: number) => formatScore(value, i18n.language)

  if (contests.length === 0) return null

  return (
    <Section id="standings">
      <Overline>{t('results.standingsOverline')}</Overline>
      <SectionTitle>{t('results.standingsTitle')}</SectionTitle>
      <p className="mt-2 max-w-2xl text-content-secondary">
        {/* Même règle pour tous les titres : le premier suffit (la liste
            n'est pas vide, on est sorti plus haut sinon). */}
        {/* Le nombre de tours et le total viennent du programme réel : un
            texte figé annonçait « trois tours, sur 300 » quoi qu'il arrive. */}
        {t('results.standingsIntro', {
          public: contests[0]?.public_weight,
          jury: contests[0]?.jury_weight,
          perRound: (contests[0]?.public_weight ?? 0) + (contests[0]?.jury_weight ?? 0),
          count: contests[0]?.rounds.length ?? 0,
          total:
            (contests[0]?.rounds.length ?? 0) *
            ((contests[0]?.public_weight ?? 0) + (contests[0]?.jury_weight ?? 0)),
        })}
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {contests.map((contest) => {
          const winnerRow = contest.rows.find((row) => row.is_winner)
          return (
            <Card
              key={contest.competition_category_id}
              className={cn(contest.winner && 'hairline-champagne bg-transparent')}
            >
              <h3 className="font-display text-heading-md text-content-primary">
                {contest.name}
              </h3>

              {!contest.proclaimed ? (
                <p className="mt-4 text-sm text-content-muted">{t('results.standingsPending')}</p>
              ) : null}

              {contest.winner ? (
                <div className="mt-5 flex items-center gap-4">
                  {contest.winner.photo_url ? (
                    <img
                      src={contest.winner.photo_url}
                      alt=""
                      className="h-16 w-16 shrink-0 rounded-full object-cover"
                      loading="lazy"
                    />
                  ) : null}
                  <div className="min-w-0">
                    <Badge tone="champagne">{t('results.winner')}</Badge>
                    <p className="mt-2 font-display text-heading-md text-champagne-300">
                      {contest.winner.name}
                    </p>
                    {winnerRow?.won_by_jury ? (
                      <p className="text-caption text-content-muted">{t('results.byJury')}</p>
                    ) : null}
                    {winnerRow?.won_by_tiebreak ? (
                      <p className="text-caption text-content-muted">{t('results.byTiebreak')}</p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {contest.proclaimed && !contest.published ? (
                <p className="mt-4 text-sm text-content-muted">{t('results.scoresPending')}</p>
              ) : null}

              {contest.rows.length > 0 ? (
                <>
                  <ol className="mt-6 space-y-4">
                    {contest.rows.map((row) => (
                      <li key={row.candidate_id}>
                        <div className="flex items-baseline justify-between gap-4">
                          <span className="flex min-w-0 items-baseline gap-3">
                            <span className="w-6 shrink-0 font-display tabular-nums text-champagne-400">
                              {row.rank}
                            </span>
                            <span className="truncate text-content-primary">{row.name}</span>
                            {row.candidate_number !== null ? (
                              <span className="font-mono text-caption text-content-muted">
                                #{row.candidate_number}
                              </span>
                            ) : null}
                          </span>
                          <span className="shrink-0 font-mono text-sm tabular-nums text-champagne-300">
                            {t('results.outOf', { score: score(row.total), max: 300 })}
                          </span>
                        </div>
                        <p className="ml-9 mt-1 text-caption text-content-muted">
                          {t('results.split', {
                            public: score(row.public_score),
                            jury: score(row.jury_score),
                          })}
                        </p>
                      </li>
                    ))}
                  </ol>

                  {/* Le détail justifie le total : un tour perdu n'empêche pas
                      de gagner le titre, et chacun doit pouvoir le lire. */}
                  <div className="mt-6 space-y-2">
                    <p className="overline text-content-muted">{t('results.perRound')}</p>
                    {contest.rounds.map((roundName, index) => (
                      <details
                        key={roundName}
                        className="rounded-lg border border-line-subtle px-4 py-3"
                      >
                        <summary className="cursor-pointer text-sm text-content-primary">
                          {roundName}
                        </summary>
                        <ul className="mt-3 space-y-3">
                          {[...contest.rows]
                            .filter((row) => row.rounds[index]?.competed)
                            .sort(
                              (a, b) =>
                                (b.rounds[index]?.score ?? 0) - (a.rounds[index]?.score ?? 0),
                            )
                            .map((row) => (
                              <li key={row.candidate_id}>
                                <RoundBreakdown name={row.name} round={row.rounds[index]!} />
                              </li>
                            ))}
                        </ul>
                      </details>
                    ))}
                  </div>
                </>
              ) : null}
            </Card>
          )
        })}
      </div>
    </Section>
  )
}

/**
 * Un tour, pour un candidat : sa note sur 100, puis d'où elle vient — les
 * voix du public et leur part, les points du jury et leur part.
 */
export function RoundBreakdown({ name, round }: { name: string; round: PublicRoundVotes }) {
  const { t, i18n } = useTranslation()
  const score = (value: number) => formatScore(value, i18n.language)
  const share = (value: number) => formatShare(value, i18n.language)

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate text-sm text-content-primary">{name}</span>
        <span className="shrink-0 font-mono text-sm tabular-nums text-champagne-300">
          {round.competed
            ? t('results.outOf', { score: score(round.score), max: 100 })
            : t('results.notCompeted')}
        </span>
      </div>
      {round.competed ? (
        <p className="mt-1 text-caption text-content-muted">
          {t('results.publicLine', {
            count: round.votes,
            share: share(round.public_share),
            points: score(round.public_score),
          })}
          {' · '}
          {t('results.juryLine', {
            points: round.jury_points,
            share: share(round.jury_share),
            score: score(round.jury_score),
          })}
        </p>
      ) : null}
    </div>
  )
}
